/**
 * shpflt-product-card — Submit-button state machine for
 * `[data-shpflt-submit-button]`.
 *
 * Visual flow on a successful add-to-cart:
 *   idle  →  loading (spinner)  →  success (check)  →  idle
 *
 * State is exposed via `data-state` on the button (`loading` / `success`).
 * A legacy `data-loading="true"` mirror is kept for CSS still keyed on it.
 * Failures revert straight back to idle (no check shown).
 *
 * Why we capture the clicked button at click time:
 *   liquid-ajax-cart v2.2 does NOT populate `requestState.formElement` in its
 *   `request-start` / `request-end` events (confirmed via console — the field
 *   is `undefined`). So we can't ask the event which form/button was
 *   responsible. Instead we record the most recently clicked submit button
 *   in the capture phase and apply state changes to it when the next
 *   ajax-cart request fires.
 *
 * Applies to product cards and the product-drawer add-to-cart button —
 * both share the `[data-shpflt-submit-button]` attribute hook.
 */

const SELECTOR = '[data-shpflt-submit-button]';
const SUCCESS_VISIBLE_MS = 1200;

/** @type {WeakMap<HTMLButtonElement, number>} */
const successTimers = new WeakMap();

/**
 * Most-recently-clicked submit button. Persists across other ajax-cart
 * requests (like `update` calls Zapiet fires while preparing the cart) and
 * is only consumed on the final `add` request. Cleared when a new submit
 * button is clicked.
 * @type {HTMLButtonElement | null}
 */
let pendingBtn = null;

/** @param {HTMLButtonElement} btn */
function setLoading(btn) {
  if (!btn) return;
  const existing = successTimers.get(btn);
  if (existing) {
    clearTimeout(existing);
    successTimers.delete(btn);
  }
  btn.dataset.state = 'loading';
  btn.dataset.loading = 'true';
}

/** @param {HTMLButtonElement} btn */
function clearState(btn) {
  if (!btn) return;
  delete btn.dataset.state;
  delete btn.dataset.loading;
}

/** @param {HTMLButtonElement} btn */
function flashSuccess(btn) {
  if (!btn) return;
  btn.dataset.state = 'success';
  delete btn.dataset.loading;
  const existing = successTimers.get(btn);
  if (existing) clearTimeout(existing);
  const timer = window.setTimeout(() => {
    if (btn.dataset.state === 'success') clearState(btn);
    successTimers.delete(btn);
  }, SUCCESS_VISIBLE_MS);
  successTimers.set(btn, timer);
}

// Capture-phase click listener: record which submit button the user just
// pressed so we can map the subsequent `add` ajax-cart event back to it.
// Other ajax-cart actions (`update`, etc.) flow through transparently and
// do NOT clear `pendingBtn`, because Zapiet often fires an `update` BEFORE
// the actual `add` and we need to keep the click reference alive until then.
document.addEventListener('click', (e) => {
  const btn = e.target?.closest?.(SELECTOR);
  if (!btn || btn.disabled) return;
  pendingBtn = btn;
}, true);

document.addEventListener('liquid-ajax-cart:request-start', (e) => {
  const requestType = e.detail?.requestState?.requestType;
  // Only show the loader for the real add — ignore Zapiet's pre-flight
  // update/change requests that happen before the add lands.
  if (requestType !== 'add') return;
  if (pendingBtn?.isConnected) setLoading(pendingBtn);
});

document.addEventListener('liquid-ajax-cart:request-end', (e) => {
  const requestState = e.detail?.requestState;
  const requestType = requestState?.requestType;
  if (requestType !== 'add') return;

  const btn = pendingBtn?.isConnected ? pendingBtn : null;
  pendingBtn = null;
  if (!btn) return;

  if (requestState?.responseData?.ok) flashSuccess(btn);
  else clearState(btn);

  // Defensive sweep: clear any stuck loading buttons elsewhere.
  for (const stuck of document.querySelectorAll(`${SELECTOR}[data-state='loading']`)) {
    if (stuck !== btn && stuck.dataset.state !== 'success') clearState(stuck);
  }
});
